import { NEWS_CONFIG } from "../config/newsConfig";
import {
  normalizeSourceList,
  normalizeSourceName,
} from "../utils/sourceModel";
import {
  ApiError,
  requestJson,
  requestWithoutBody,
} from "./apiClient";


const normalizeCountMap = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  return Object.entries(value).reduce((result, [source, count]) => {
    const normalizedSource = normalizeSourceName(source);
    const numericCount = Number(count);

    if (
      normalizedSource &&
      Number.isFinite(numericCount) &&
      numericCount > 0
    ) {
      result[normalizedSource] = Math.floor(numericCount);
    }

    return result;
  }, {});
};

const getItemsFromResponse = (data) => {
  const items = Array.isArray(data)
    ? data
    : Array.isArray(data?.items)
    ? data.items
    : [];

  return items;
};


export const verifyNewsApiConnection = async ({ signal } = {}) => {
  const params = new URLSearchParams({
    limit: "1",
    connection_probe: String(Date.now()),
  });

  await requestJson({
    url: `${NEWS_CONFIG.apiUrl}?${params.toString()}`,
    operation: "connection-probe",
    signal,
    timeoutMs: NEWS_CONFIG.requestTimeoutsMs.connectionProbe,
    cache: "no-store",
    defaultMessage: "ارتباط با سرور برقرار نشد",
  });

  return true;
};

export const fetchLatestNews = async ({
  cursor,
  isInitial,
  signal,
  targetInsertId = 0,
  pollingContext = {},
  sources = [],
} = {}) => {
  const params = new URLSearchParams();

  if (isInitial) {
    params.set(
      "limit",
      String(NEWS_CONFIG.initialBatchSize)
    );
    params.set("include_insert_watermark", "1");

    const initialSources = normalizeSourceList(sources);

    if (initialSources.length > 0) {
      params.set("sources", initialSources.join(","));
    }
  } else {
    params.set("type", "poll_inserts");
    params.set(
      "after_insert_id",
      String(Number(cursor?.id) || 0)
    );
    params.set(
      "limit",
      String(NEWS_CONFIG.insertPollingBatchSize)
    );

    if (Number(targetInsertId) > 0) {
      params.set(
        "target_insert_id",
        String(Math.floor(Number(targetInsertId)))
      );
    }

    if (pollingContext?.topCursor?.sortDate) {
      params.set(
        "top_sort_date",
        pollingContext.topCursor.sortDate
      );
      params.set(
        "top_id",
        String(Number(pollingContext.topCursor.id) || 0)
      );
    }

    if (pollingContext?.deepestReadCursor?.sortDate) {
      params.set(
        "deepest_read_sort_date",
        pollingContext.deepestReadCursor.sortDate
      );
      params.set(
        "deepest_read_id",
        String(Number(pollingContext.deepestReadCursor.id) || 0)
      );
    }

    const pollingSources = normalizeSourceList(
      pollingContext?.sources || []
    );

    if (pollingSources.length > 0) {
      params.set("sources", pollingSources.join(","));
    }
  }

  const data = await requestJson({
    url: `${NEWS_CONFIG.apiUrl}?${params.toString()}`,
    operation: isInitial ? "latest-initial" : "insert-polling",
    signal,
    timeoutMs: isInitial
      ? NEWS_CONFIG.requestTimeoutsMs.initial
      : NEWS_CONFIG.requestTimeoutsMs.polling,
    cache: "no-store",
    defaultMessage: isInitial
      ? "دریافت خبرها با مشکل مواجه شد"
      : "بروزرسانی خبرها انجام نشد",
  });

  if (!isInitial && data?.type !== "poll_inserts") {
    throw new ApiError({
      message: "نسخه API از Polling مبتنی بر ID پشتیبانی نمی‌کند",
      kind: "server",
      operation: "insert-polling",
      retryable: true,
    });
  }

  const items = getItemsFromResponse(data);
  const maximumReturnedId = items.reduce((maximum, item) => {
    const id = Number(item?.id);

    return Number.isFinite(id)
      ? Math.max(maximum, id)
      : maximum;
  }, 0);

  const requestedCursorId = Number(cursor?.id) || 0;
  const hasInsertWatermark = Object.prototype.hasOwnProperty.call(
    data || {},
    "insert_watermark_id"
  );
  const insertWatermarkId = Number(data?.insert_watermark_id);
  const nextInsertId = Number(data?.next_insert_id);

  const targetId = Number(data?.target_insert_id);
  const totalInserted = Number(data?.total_inserted);
  const backlogThreshold = Number(data?.backlog_threshold);
  const freshCount = Number(data?.fresh_count);
  const readBackfillCount = Number(data?.read_backfill_count);

  return {
    items,
    mode: data?.mode === "large_backlog"
      ? "large_backlog"
      : "paged",
    hasInsertWatermark,
    insertWatermarkId: Number.isFinite(insertWatermarkId)
      ? Math.max(0, insertWatermarkId)
      : 0,
    targetInsertId: Number.isFinite(targetId)
      ? Math.max(requestedCursorId, targetId)
      : Math.max(requestedCursorId, Number(targetInsertId) || 0),
    nextInsertId: Number.isFinite(nextInsertId)
      ? Math.max(requestedCursorId, nextInsertId)
      : Math.max(requestedCursorId, maximumReturnedId),
    hasMore: Boolean(data?.has_more),
    totalInserted: Number.isFinite(totalInserted)
      ? Math.max(0, Math.floor(totalInserted))
      : 0,
    backlogThreshold: Number.isFinite(backlogThreshold)
      ? Math.max(0, Math.floor(backlogThreshold))
      : NEWS_CONFIG.largeBacklogThreshold,
    freshCount: Number.isFinite(freshCount)
      ? Math.max(0, Math.floor(freshCount))
      : 0,
    freshCountsBySource: normalizeCountMap(
      data?.fresh_counts_by_source
    ),
    readBackfillCount: Number.isFinite(readBackfillCount)
      ? Math.max(0, Math.floor(readBackfillCount))
      : 0,
    readBackfillCountsBySource: normalizeCountMap(
      data?.read_backfill_counts_by_source
    ),
    deferredItems: Array.isArray(data?.deferred_items)
      ? data.deferred_items
      : [],
  };
};

export const fetchOlderNewsBatch = async ({
  cursor,
  sources = [],
  limit = NEWS_CONFIG.loadMoreBatchSize,
  signal,
} = {}) => {
  const params = new URLSearchParams({
    limit: String(limit),
  });

  if (cursor?.id) {
    params.set("before_id", String(cursor.id));
  }

  if (cursor?.sortDate) {
    params.set("before_sort_date", cursor.sortDate);
  }

  if (sources.length > 0) {
    params.set("sources", normalizeSourceList(sources).join(","));
  }

  const data = await requestJson({
    url: `${NEWS_CONFIG.apiUrl}?${params.toString()}`,
    operation: "older-news",
    signal,
    timeoutMs: NEWS_CONFIG.requestTimeoutsMs.loadMore,
    cache: "no-store",
    defaultMessage: "دریافت خبرهای بیشتر انجام نشد",
  });

  return getItemsFromResponse(data);
};

export const fetchSourceCatalog = async ({ signal } = {}) => {
  const data = await requestJson({
    url: `${NEWS_CONFIG.apiUrl}?type=sources`,
    operation: "source-catalog",
    signal,
    timeoutMs: NEWS_CONFIG.requestTimeoutsMs.sourceMetadata,
    cache: "no-store",
    defaultMessage: "دریافت فهرست منابع انجام نشد",
  });

  const sources = Array.isArray(data) ? data : data?.sources;

  return Array.isArray(sources) ? normalizeSourceList(sources) : [];
};

export const fetchSourceCounts = async ({ since = "", signal } = {}) => {
  const params = new URLSearchParams({
    type: "source_counts",
  });

  if (since) {
    params.set("since", since);
  }

  const data = await requestJson({
    url: `${NEWS_CONFIG.apiUrl}?${params.toString()}`,
    operation: "source-counts",
    signal,
    timeoutMs: NEWS_CONFIG.requestTimeoutsMs.sourceMetadata,
    cache: "no-store",
    defaultMessage: "دریافت تعداد خبرهای منابع انجام نشد",
  });

  const rows = Array.isArray(data) ? data : data?.counts;

  if (!Array.isArray(rows)) return {};

  return rows.reduce((counts, row) => {
    if (!row?.source) return counts;

    const source = normalizeSourceName(row.source);
    if (!source) return counts;

    counts[source] =
      (counts[source] || 0) + (Number(row.count) || 0);
    return counts;
  }, {});
};

export const fetchMostVisitedNewsBatch = async ({
  filter = "24h",
  sources = [],
  limit = NEWS_CONFIG.mostVisitedLimit,
  signal,
} = {}) => {
  const params = new URLSearchParams({
    type: "most_visited",
    period: filter,
    limit: String(limit),
  });

  if (sources.length > 0) {
    params.set("sources", normalizeSourceList(sources).join(","));
  }

  const data = await requestJson({
    url: `${NEWS_CONFIG.apiUrl}?${params.toString()}`,
    operation: "most-visited",
    signal,
    timeoutMs: NEWS_CONFIG.requestTimeoutsMs.mostVisited,
    defaultMessage: "دریافت پربازدیدها انجام نشد",
  });

  return getItemsFromResponse(data);
};

export const fetchSingleNews = async ({ id, signal } = {}) => {
  if (!id) return null;

  const params = new URLSearchParams({
    id: String(id),
    type: "single",
  });

  const data = await requestJson({
    url: `${NEWS_CONFIG.apiUrl}?${params.toString()}`,
    operation: "single-news",
    signal,
    timeoutMs: NEWS_CONFIG.requestTimeoutsMs.sharedNews,
    cache: "no-store",
    notFoundMessage: "خبر پیدا نشد",
    defaultMessage: "دریافت خبر انجام نشد",
  });

  const item =
    data?.item ||
    (Array.isArray(data?.items) ? data.items[0] : null) ||
    (Array.isArray(data)
      ? data.find((entry) => String(entry?.id) === String(id))
      : null);

  if (!item) {
    throw new ApiError({
      message: "خبر پیدا نشد",
      kind: "not-found",
      operation: "single-news",
      retryable: false,
    });
  }

  return item;
};

export const recordNewsViewRequest = async (id) => {
  if (id === undefined || id === null) return;

  try {
    await requestWithoutBody({
      url: `${NEWS_CONFIG.apiUrl}?action=view`,
      operation: "record-view",
      timeoutMs: NEWS_CONFIG.requestTimeoutsMs.recordView,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id }),
      keepalive: true,
      defaultMessage: "ثبت بازدید انجام نشد",
    });
  } catch (error) {
    /*
    |--------------------------------------------------------------------------
    | View tracking is intentionally silent.
    |--------------------------------------------------------------------------
    |
    | A tracking failure must never interrupt opening or reading a news item.
    |
    */
    if (process.env.NODE_ENV !== "production") {
      console.warn("record-view failed", error);
    }
  }
};
